import { createElement, useRef, useState, Fragment, forwardRef, useImperativeHandle } from 'react';
import { Icon } from '../Icon';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { Text } from '../Text';
import { Popover } from '../Popover/Popover';
import { Link } from '../Link';
import { Button, ButtonVariant } from '../Button';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { isNullish } from '@sas/nova-core/typeguards';
import { Flow } from '../Layouts/Flow';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-ObjectMarker-gui-icu.properties';
import { setRef } from '../../util/ref-util';
import { getIconAndLabelTextFromType } from './__internal__/getIconAndLabelTextFromType';
import { ObjectMarkerProps } from './types';

import cx from 'classnames';
import cn from './ObjectMarker.css.json';
import './ObjectMarker.css';

const useHtmlId = createUseHtmlId('ObjectMarker');
export const useObjectMarkerLocalizer = createUseTextLocalizer(bundle);

export const ObjectMarker = forwardRef<HTMLElement, ObjectMarkerProps>(function ObjectMarker(
  {
    variant = '',
    disabled = false,
    focusable = true,
    hideIcon = false,
    hideLabel = false,
    ...props
  },
  forwardedRef
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [popperIsOpen, setPopperIsOpen] = useState(false);
  const localize = useObjectMarkerLocalizer();
  const { labelText: labelFromType, icon } = getIconAndLabelTextFromType(props.type, localize);
  const hasCustomLabel = !isNullish(props.text);
  const labelText = props.text ?? labelFromType;
  const hasPopover = !!(props.popperText || props.popoverProps) && !hideLabel;
  const dt = useDesignTokens();
  const tokens = {
    ...renderInlineCssVars(
      {
        xxs: dt.spacing.core.xxs,
        xs: dt.spacing.core.xs,
      },
      'object-marker-spacing'
    ),
  };

  const [iconId, labelId] = useHtmlId(2);
  useImperativeHandle(props.popperRef, () => ({
    setPopperIsOpen: (value: boolean) => {
      setPopperIsOpen(value);
    },
  }));

  let labelComponent = null;
  if (!hideLabel) {
    labelComponent = hasPopover ? (
      <Link id={labelId} tabIndex={-1} wrapText={false} disabled={disabled}>
        {labelText}
      </Link>
    ) : (
      <Text id={labelId} disabled={disabled} wrapText={false}>
        {labelText}
      </Text>
    );
  }

  let iconTooltip = '';
  {
    // an explicit icon tooltip takes first precedence
    if (!isNullish(props.iconTooltip)) iconTooltip = props.iconTooltip;
    // when there is anything custom about the label, whether by text or visibility,
    // the icon tooltip should be the default text associated with the icon
    else if (hasCustomLabel || hideLabel) iconTooltip = labelFromType;
    // otherwise, the tooltip will be suppressed (leaving it as '')
  }

  let iconAriaLabel: string | undefined;
  {
    // an explicit icon aria-label takes first precedence
    if (!isNullish(props.iconAriaLabel)) iconAriaLabel = props.iconAriaLabel;
    // when it's been determined that a tooltip should display on the icon,
    // (meaning Icon's "tooltip" prop will be populated with a value),
    // omit populating the aria-label, leaving it to be internally handled by Icon
    else if (iconTooltip !== '') iconAriaLabel = undefined;
    // otherwise, use an aria-label that matches the default text associated with
    // the Icon (ignore any custom label (props.text))
    else iconAriaLabel = labelFromType;
  }

  // determine what the ObjectMarker root aria-labelledby value should be.
  // will either point to the icon, label, or both
  const ariaLabelledByIds: string[] = [];
  if (
    !hideIcon &&
    ((iconTooltip === '' && hasCustomLabel) ||
      iconTooltip !== '' ||
      !isNullish(props.iconAriaLabel))
  )
    ariaLabelledByIds.push(iconId);

  if (!hideLabel) ariaLabelledByIds.push(labelId);
  if (props.ariaLabelledBy) ariaLabelledByIds.push(props.ariaLabelledBy);
  const ariaLabelledBy = ariaLabelledByIds.join(' ');

  let iconAriaHidden = true;
  {
    // explicitly setting iconAriaHidden to false takes first precedence
    if (props.iconAriaHidden === false) iconAriaHidden = false;
    // when both iconTooltip and iconAriaLabel is "suppressed", iconAriaHidden should be true
    else if (props.iconTooltip === '' && iconAriaLabel === '') iconAriaHidden = true;
    // when the consumer has set any prop to a value that would affect
    // the icon's aria information, the icon should not be aria-hidden
    else if (
      !isNullish(props.text) ||
      !isNullish(props.iconAriaLabel) ||
      !isNullish(props.iconTooltip)
    )
      iconAriaHidden = false;
    // static OM with icon only should have iconAriaHidden should be false
    else if (!hasPopover && hideLabel) iconAriaHidden = false;
  }

  return (
    <Fragment>
      {hasPopover ? (
        <Button
          id={props.id}
          variant={ButtonVariant.Light}
          tokens={() => ({ bg: 'transparent', filter: 'none' })}
          style={{
            textDecoration: 'underline',
            border: 0,
            padding: 0,
            ...tokens,
            ...props.style,
            ['--button-spacing-xs' as any]: '0',
          }}
          ref={(div) => {
            buttonRef.current = div;
            setRef(forwardedRef, div);
          }}
          ariaLabel={props.ariaLabel}
          ariaLabelledBy={props.ariaLabelledBy}
          ariaDescribedBy={props.ariaDescribedBy}
          aria-disabled={disabled}
          className={cx(
            cn.objectMarker,
            hasPopover && cn.objectMarkerLink,
            disabled && cn.disabled,
            props.className
          )}
          onPress={() => {
            setPopperIsOpen(true);
          }}
          focusable={focusable}
          disabled={disabled}
          testId={props.testId}
          text={!hideLabel ? labelText : undefined}
          icon={
            !hideIcon && icon
              ? {
                  id: iconId,
                  className: cx(
                    props.type === 'AnimatedInProgress' ? cn.animatedInProgress : null,
                    cn.icon
                  ),
                  style: { marginInlineEnd: 2 },
                  icon,
                  tokens: () => {
                    if (
                      props.type === 'Higher' ||
                      props.type === 'Lower' ||
                      props.type === 'SignificantlyHigher' ||
                      props.type === 'SignificantlyLower' ||
                      props.type === 'NegativeSentimentalLevel' ||
                      props.type === 'NeutralSentimentalLevel' ||
                      props.type === 'PositiveSentimentalLevel' ||
                      props.type === 'TrendDownStatus' ||
                      props.type === 'TrendNoChangeStatus' ||
                      props.type === 'TrendUpStatus'
                    ) {
                      if (variant === 'positive') {
                        switch (dt.themeVariant) {
                          case 'dark':
                            return { fg: 'hsla(165, 68%, 47%, 1)' };
                          case 'high-contrast':
                            return { fg: 'hsla(91, 41%, 61%, 1)' };
                          default:
                            return { fg: 'hsla(158, 79%, 27%, 1)' };
                        }
                      } else if (variant === 'negative') {
                        switch (dt.themeVariant) {
                          case 'dark':
                            return { fg: 'hsla(351, 100%, 77%, 1)' };
                          case 'high-contrast':
                            return { fg: 'hsla(4, 57%, 63%, 1)' };
                          default:
                            return { fg: 'hsla(0, 44%, 45%, 1)' };
                        }
                      } else if (variant === 'flat') {
                        switch (dt.themeVariant) {
                          case 'dark':
                            return { fg: 'hsla(48, 78%, 64%, 1)' };
                          case 'high-contrast':
                            return { fg: 'hsla(51, 87%, 74%, 1)' };
                          default:
                            return { fg: 'hsla(45, 67%, 47%, 1)' };
                        }
                      } else {
                        return {};
                      }
                    } else {
                      return {};
                    }
                  },
                }
              : undefined
          }
        />
      ) : (
        <Flow
          id={props.id}
          spacing="xxs"
          style={{ ...tokens, ...props.style }}
          ref={(div) => {
            rootRef.current = div;
            setRef(forwardedRef, div);
          }}
          aria-label={props.ariaLabel}
          aria-labelledby={hasPopover ? ariaLabelledBy : props.ariaLabelledBy}
          aria-describedby={props.ariaDescribedBy}
          aria-disabled={disabled}
          className={cx(
            cn.objectMarker,
            hasPopover && cn.objectMarkerLink,
            disabled && cn.disabled,
            props.className
          )}
          tabIndex={focusable ? 0 : -1}
          role={!hideLabel && props['data-with-labels'] ? 'textbox' : undefined}
          aria-readonly={!hideLabel && props['data-with-labels'] ? true : undefined}
          onClick={(event) => {
            if (!disabled && hasPopover) {
              event.preventDefault();
              setPopperIsOpen(true);
            }
          }}
          onKeyDown={(event) =>
            (event.key === 'Enter' || event.key === ' ') &&
            disabled === false &&
            hasPopover &&
            setPopperIsOpen(true)
          }
          data-testid={props.testId}
        >
          {!hideIcon && icon ? (
            <Icon
              id={iconId}
              className={cx(
                props.type === 'AnimatedInProgress' ? cn.animatedInProgress : null,
                cn.icon
              )}
              ariaHidden={iconAriaHidden}
              ariaLabel={iconAriaLabel}
              tooltip={iconTooltip}
              icon={icon}
              disabled={disabled}
              tokens={() => {
                if (
                  props.type === 'Higher' ||
                  props.type === 'Lower' ||
                  props.type === 'SignificantlyHigher' ||
                  props.type === 'SignificantlyLower' ||
                  props.type === 'NegativeSentimentalLevel' ||
                  props.type === 'NeutralSentimentalLevel' ||
                  props.type === 'PositiveSentimentalLevel' ||
                  props.type === 'TrendDownStatus' ||
                  props.type === 'TrendNoChangeStatus' ||
                  props.type === 'TrendUpStatus'
                ) {
                  if (variant === 'positive') {
                    switch (dt.themeVariant) {
                      case 'dark':
                        return { fg: 'hsla(165, 68%, 47%, 1)' };
                      case 'high-contrast':
                        return { fg: 'hsla(91, 41%, 61%, 1)' };
                      default:
                        return { fg: 'hsla(158, 79%, 27%, 1)' };
                    }
                  } else if (variant === 'negative') {
                    switch (dt.themeVariant) {
                      case 'dark':
                        return { fg: 'hsla(351, 100%, 77%, 1)' };
                      case 'high-contrast':
                        return { fg: 'hsla(4, 57%, 63%, 1)' };
                      default:
                        return { fg: 'hsla(0, 44%, 45%, 1)' };
                    }
                  } else if (variant === 'flat') {
                    switch (dt.themeVariant) {
                      case 'dark':
                        return { fg: 'hsla(48, 78%, 64%, 1)' };
                      case 'high-contrast':
                        return { fg: 'hsla(51, 87%, 74%, 1)' };
                      default:
                        return { fg: 'hsla(45, 67%, 47%, 1)' };
                    }
                  } else {
                    return {};
                  }
                } else {
                  return {};
                }
              }}
            />
          ) : null}
          {labelComponent}
        </Flow>
      )}
      <Popover
        isOpen={popperIsOpen}
        referenceElementRef={hasPopover ? buttonRef : rootRef}
        onClose={() => setPopperIsOpen(false)}
        ariaLabel={localize('popover.information.label')}
        focusable
        role="dialog"
        style={{ maxWidth: '24rem', maxHeight: '50vh' }}
        children={props.popperText ?? ''}
        {...props.popoverProps}
      />
    </Fragment>
  );
});
